import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { ProfileVideo } from "../types";
import { VideoPlayer } from "./ProfileVideo";

type Item = ProfileVideo & { displayName: string; userId: string; submittedAt: string };
const reasons = [
  ["not_a_person", "В кадре нет владельца анкеты"],
  ["face_not_visible", "Лицо плохо видно"],
  ["multiple_people", "В кадре несколько человек"],
  ["explicit_content", "Недопустимый контент"],
  ["text_or_contact_info", "Текст или контактные данные"],
  ["low_quality", "Низкое качество"],
  ["other", "Другая причина"],
];
export function AdminVideos({ onChanged }: { onChanged(): Promise<void> }) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("face_not_visible");
  const load = useCallback(async () => {
    setError("");
    try {
      setItems((await api<{ items: Item[] }>("/v1/admin/moderation/videos")).items);
    } catch {
      setError("Не удалось загрузить очередь видео.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const current = items[0];
  const decide = async (decision: "approve" | "reject") => {
    if (!current || busy) return;
    setBusy(true);
    setError("");
    try {
      await api(`/v1/admin/moderation/videos/${current.id}/decision`, {
        method: "POST",
        body: JSON.stringify(decision === "approve" ? { decision } : { decision, reasonCode: reason }),
      });
      setRejecting(false);
      await load();
      await onChanged().catch(() => undefined);
    } catch {
      setError("Не удалось сохранить решение. Обновите очередь и повторите попытку.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="admin-video-queue">
      {loading ? (
        <p role="status">Загружаем видео…</p>
      ) : current ? (
        <article className="moderation-card">
          <VideoPlayer key={current.id} video={current} />
          <div className="moderation-meta">
            <h2>{current.displayName}</h2>
            <span>{Math.round(current.durationMs / 1000)} сек.</span>
          </div>
          {rejecting ? (
            <div className="moderation-actions">
              <label className="reference-field">
                Причина отклонения
                <select
                  className="reference-input"
                  value={reason}
                  disabled={busy}
                  onChange={(e) => setReason(e.target.value)}
                >
                  {reasons.map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <button className="reference-row" type="button" disabled={busy} onClick={() => void decide("reject")}>
                Отклонить видео
              </button>
              <button className="reference-row" type="button" disabled={busy} onClick={() => setRejecting(false)}>
                Отмена
              </button>
            </div>
          ) : (
            <div className="moderation-actions">
              <button
                type="button"
                className="reference-primary"
                disabled={busy}
                onClick={() => void decide("approve")}
              >
                Одобрить видео
              </button>
              <button type="button" className="reference-row" disabled={busy} onClick={() => setRejecting(true)}>
                Отклонить
              </button>
            </div>
          )}
        </article>
      ) : (
        !error && <p className="reference-note">Новых видео на проверку нет.</p>
      )}
      {error && (
        <p className="reference-error" role="alert">
          {error}
        </p>
      )}
      <button type="button" className="reference-row" disabled={busy} onClick={() => void load()}>
        Обновить очередь видео
      </button>
    </section>
  );
}
